// TEST DATA for the Számlázás page (api/billing.js decides when it is used).
//
// One in-memory account per brand: a card on file, billing details, and a
// usage history that started with a trial month five months ago. The invoices
// are the past months' usage totals, so every number on the page agrees.
// No publishableKey: the test data never loads real Stripe.

import { hash, rng } from './embedMock';
import { PRICING, periodKey } from './billingPricing';

const CURRENCY = 'HUF';
const HISTORY_MONTHS = 5; // the trial month is this many months ago

const accounts = new Map();

function monthsAgo(period, today = new Date()) {
  const [y, m] = String(period).split('-').map(Number);
  return (today.getFullYear() - y) * 12 + (today.getMonth() + 1 - m);
}

function periodBounds(period) {
  const [y, m] = period.split('-').map(Number);
  return { start: new Date(Date.UTC(y, m - 1, 1)), end: new Date(Date.UTC(y, m, 0, 23, 59, 59)) };
}

export function mockUsage(brandId, period = periodKey()) {
  const p = PRICING[CURRENCY];
  const ago = monthsAgo(period);
  const base = {
    period,
    currency: CURRENCY,
    includedViews: p.includedViews,
    overageCap: p.overageCap,
    commissionRate: p.commissionRate,
  };
  if (ago < 0 || ago > HISTORY_MONTHS) {
    return {
      ...base, baseFee: 0, trial: false, uniqueViews: 0, overageViews: 0, overageAmount: 0,
      matchedPurchases: 0, matchedRevenue: 0, commissionAmount: 0, estimatedTotal: 0,
    };
  }
  const next = rng(hash(`usage:${brandId}:${period}`));
  // A month that grew past the cap (two months ago), the rest 700–1 900.
  let uniqueViews = ago === 2 ? 5480 : Math.round(700 + next() * 1200);
  // The trial month: only the base fee is waived — it ran past the included
  // matchings, so its overage and commission still count.
  if (ago === HISTORY_MONTHS) uniqueViews = Math.round(620 + next() * 120);
  if (ago === 0) {
    // This month so far.
    const today = new Date();
    const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    uniqueViews = Math.round(uniqueViews * (today.getDate() / daysInMonth));
  }
  const trial = ago === HISTORY_MONTHS;
  const overageViews = Math.max(0, uniqueViews - p.includedViews);
  const overageAmount = Math.min(overageViews * p.perView, p.overageCap);
  const matchedPurchases = Math.round(uniqueViews * (0.014 + next() * 0.008));
  const matchedRevenue = Math.round(matchedPurchases * (14000 + next() * 9000) / 10) * 10;
  const commissionAmount = Math.round(matchedRevenue * p.commissionRate);
  const baseFee = trial ? 0 : p.baseFee;
  return {
    ...base,
    baseFee,
    trial,
    uniqueViews,
    overageViews,
    overageAmount,
    matchedPurchases,
    matchedRevenue,
    commissionAmount,
    estimatedTotal: baseFee + overageAmount + commissionAmount,
  };
}

// A one-page PDF with the invoice's number and total, as a blob URL, so the
// PDF link on test data opens something real. Null where blobs are missing
// (tests).
function mockPdfUrl(number, usage) {
  if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function' || typeof Blob === 'undefined') return null;
  const text = `TESZTSZAMLA ${number} - ${usage.period} - ${usage.estimatedTotal} ${usage.currency}`;
  const stream = `BT /F1 14 Tf 60 760 Td (${text}) Tj ET`;
  const objs = [
    '<< /Type /Catalog /Pages 2 0 R >>',
    '<< /Type /Pages /Kids [3 0 R] /Count 1 >>',
    '<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Contents 4 0 R /Resources << /Font << /F1 5 0 R >> >> >>',
    `<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`,
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>',
  ];
  let pdf = '%PDF-1.4\n';
  const offsets = objs.map((body, i) => {
    const at = pdf.length;
    pdf += `${i + 1} 0 obj\n${body}\nendobj\n`;
    return at;
  });
  const xref = pdf.length;
  pdf += `xref\n0 ${objs.length + 1}\n0000000000 65535 f \n${offsets.map((o) => `${String(o).padStart(10, '0')} 00000 n \n`).join('')}`;
  pdf += `trailer\n<< /Size ${objs.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF`;
  return URL.createObjectURL(new Blob([pdf], { type: 'application/pdf' }));
}

function invoicesFor(brandId) {
  const list = [];
  for (let ago = 1; ago < HISTORY_MONTHS; ago += 1) {
    const today = new Date();
    const period = periodKey(new Date(today.getFullYear(), today.getMonth() - ago, 1));
    const usage = mockUsage(brandId, period);
    const { start, end } = periodBounds(period);
    const number = `OUTF-${period.replace('-', '')}-${String(hash(brandId) % 900 + 100)}`;
    list.push({
      id: `in_mock_${period}`,
      number,
      amount: usage.estimatedTotal,
      currency: usage.currency,
      status: 'paid',
      periodStart: start.toISOString(),
      periodEnd: end.toISOString(),
      hostedUrl: null,
      pdfUrl: mockPdfUrl(number, usage),
    });
  }
  return list;
}

// `?billingMockCard=missing|expired|failed` starts the test account that way
// (to look at the card gate); the default has a valid card.
function initialCard() {
  const want = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('billingMockCard') : null;
  if (want === 'missing') return { paymentMethod: null, lastCharge: null };
  if (want === 'expired') return { paymentMethod: { brand: 'visa', last4: '4242', expMonth: 1, expYear: 2024 }, lastCharge: null };
  if (want === 'failed') return { paymentMethod: { brand: 'mastercard', last4: '4444', expMonth: 9, expYear: 2029 }, lastCharge: 'failed' };
  return { paymentMethod: { brand: 'visa', last4: '4242', expMonth: 8, expYear: 2028 }, lastCharge: null };
}

/// The test account's card status, as the API derives it.
export function mockCardStatus(a, today = new Date()) {
  if (!a.paymentMethod) return 'missing';
  if (new Date(a.paymentMethod.expYear, a.paymentMethod.expMonth, 1) <= today) return 'expired';
  if (a.lastCharge === 'failed') return 'failed';
  return 'valid';
}

function account(brandId) {
  if (!accounts.has(brandId)) {
    const card = initialCard();
    accounts.set(brandId, {
      customer: {
        name: 'Nova Atelier Kft.',
        email: 'szamlazas@nova-atelier.hu',
        taxId: '12345678-2-42',
        address: { line1: 'Király utca 26.', line2: '', city: 'Budapest', postalCode: '1061', country: 'HU' },
      },
      paymentMethod: card.paymentMethod,
      lastCharge: card.lastCharge,
      invoices: invoicesFor(brandId),
    });
  }
  return accounts.get(brandId);
}

const clone = (v) => JSON.parse(JSON.stringify(v));

export function mockBilling(brandId) {
  const a = account(brandId);
  const { lastCharge, ...rest } = a;
  return clone({ ...rest, cardStatus: mockCardStatus(a), publishableKey: null, usage: mockUsage(brandId) });
}

export function mockSetupIntent() {
  return { clientSecret: `seti_mock_${Date.now()}_secret_mock` };
}

export function mockSavePaymentMethod(brandId, paymentMethodId, card) {
  const a = account(brandId);
  a.paymentMethod = card
    ? { brand: card.brand, last4: card.last4, expMonth: card.expMonth, expYear: card.expYear }
    : { brand: 'visa', last4: '4242', expMonth: 12, expYear: new Date().getFullYear() + 3 };
  a.lastCharge = null; // a new card clears a failed charge
  return clone({ paymentMethod: a.paymentMethod });
}

export function mockRemovePaymentMethod(brandId) {
  account(brandId).paymentMethod = null;
  return { paymentMethod: null };
}

export function mockSaveDetails(brandId, details) {
  const a = account(brandId);
  a.customer = clone(details);
  return clone({ customer: a.customer });
}

/// Tests start from a fresh account.
export function resetBillingMock() {
  accounts.clear();
}
